import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { listUsers, createTherapist, updateTherapist } from '../../lib/functions';
import { Spinner } from '../../components/ui/Spinner';

export function TherapistForm() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(uid);

  const [loading, setLoading] = useState(isEdit);
  const [notFound, setNotFound] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isEdit || !uid) return;
    void (async () => {
      try {
        const users = await listUsers();
        const existing = users.find((u) => u.uid === uid);
        if (!existing) {
          setNotFound(true);
        } else {
          setDisplayName(existing.displayName ?? '');
          setEmail(existing.email ?? '');
        }
      } catch (err) {
        console.error(err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, uid]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) return setError('Informe o e-mail.');

    if (!isEdit) {
      if (!password) return setError('Informe a senha.');
      if (password.length < 6) return setError('A senha deve ter pelo menos 6 caracteres.');
      if (password !== confirmPassword) return setError('As senhas não coincidem.');
    }

    setBusy(true);
    try {
      if (isEdit && uid) {
        await updateTherapist(uid, email.trim(), displayName.trim() || undefined);
        navigate(`/admin/therapists/${uid}`);
      } else {
        const newUid = await createTherapist(email.trim(), password, displayName.trim() || undefined);
        navigate(`/admin/therapists/${newUid}`);
      }
    } catch (err) {
      console.error(err);
      const code = (err as { code?: string })?.code;
      setError(
        code === 'functions/already-exists'
          ? 'Já existe uma conta com este e-mail.'
          : 'Não foi possível salvar. Tente novamente.'
      );
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="empty-state">
        Terapeuta não encontrado.{' '}
        <a onClick={() => navigate('/admin/therapists')} style={{ cursor: 'pointer' }}>
          Voltar
        </a>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? 'Editar terapeuta' : 'Novo terapeuta'}</h1>
      </div>

      <div className="card" style={{ maxWidth: 520 }}>
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="name">Nome</label>
            <input
              id="name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Nome do terapeuta"
            />
          </div>

          <div className="field">
            <label htmlFor="email">E-mail</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="terapeuta@exemplo.com"
            />
          </div>

          {!isEdit && (
            <>
              <div className="field">
                <label htmlFor="password">Senha</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo de 6 caracteres"
                />
              </div>
              <div className="field">
                <label htmlFor="confirmPassword">Confirmar senha</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <p className="muted" style={{ fontSize: 13 }}>
                A conta será criada com acesso de terapeuta. Compartilhe o e-mail e a senha com a pessoa responsável.
              </p>
            </>
          )}

          {error && <div className="field error">{error}</div>}

          <div className="row" style={{ marginTop: 8 }}>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Salvando…' : 'Salvar'}
            </button>
            <button className="btn secondary" type="button" onClick={() => navigate(-1)}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
